import React, { useEffect } from "react"
import { useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { useLocalization } from "@fluent/react"
import { useSnackbar } from "notistack"
import Button from "@material-ui/core/Button"
import { Logger } from "shared/Helpers/logging"
import { useFirebase } from "shared/Modules/Cordova/hooks/useFirebase"
import { getNotificationCount, notificationReceived } from "./notificationActions"
import { extractNotificationData, RawNotificationPayload } from "./notificationPayload"
import { messagesUrlForMessage } from "./notificationNavigation"

// The OS hides notifications while the app is foregrounded, so surface them in a snackbar that deep links to the message
export function useForegroundNotifications() {
    const firebase = useFirebase()
    const history = useHistory()
    const dispatch = useDispatch()
    const { enqueueSnackbar, closeSnackbar } = useSnackbar()
    const { l10n } = useLocalization()

    useEffect(() => {
        if (!firebase) { return }

        const logger = new Logger("notifications")

        firebase.onMessage(
            (payload: RawNotificationPayload) => {
                const { title, body, messageId, wasTapped } = extractNotificationData(payload)

                dispatch(getNotificationCount())
                dispatch(notificationReceived())

                const target = messagesUrlForMessage(messageId)

                if (wasTapped) {
                    history.push(target)
                    return
                }

                const message = title && body
                    ? `${title}: ${body}`
                    : (title || body || l10n.getString("notification-snackbar-default-message", null, "New message"))

                enqueueSnackbar(message, {
                    variant: "info",
                    preventDuplicate: true,
                    action: (key) => (
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => {
                                closeSnackbar(key)
                                history.push(target)
                            }}
                        >
                            {l10n.getString("notification-snackbar-action-view", null, "View")}
                        </Button>
                    ),
                })
            },
            (error) => logger.warn("Error while listening for push notifications", error),
        )
    }, [firebase])
}
